import { Loader2 } from "lucide-react";
import { setsFor, type SpreadRxNutrient } from "../../lib/spreadRx";
import type { DashboardField } from "../../types/contract";

export const NUTRIENT_LABEL: Record<SpreadRxNutrient, string> = {
  lime: "Lime",
  p2o5: "P₂O₅",
  k2o: "K₂O",
};

export const NUTRIENT_WORD: Record<SpreadRxNutrient, string> = {
  lime: "lime",
  p2o5: "phosphorus",
  k2o: "potassium",
};

interface SpreadRow {
  setId: string;
  loading: boolean;
  error: string | null;
}

function SpreadRxSelect({
  nutrient,
  fieldName,
  setId,
  options,
  hydrating,
  onPick,
}: {
  nutrient: SpreadRxNutrient;
  fieldName: string;
  setId: string;
  options: { id: string; name: string; createdAt: string }[];
  hydrating: boolean;
  onPick: (setId: string) => void;
}) {
  if (options.length === 0) {
    // never claim "none" before the server reconcile has answered — the cache
    // is per-origin, so a miss is not evidence the field has no Rx.
    return (
      <span className="text-[11px] text-muted">
        {hydrating
          ? `Reading saved ${NUTRIENT_WORD[nutrient]} Rx…`
          : `No saved ${NUTRIENT_WORD[nutrient]} Rx — build one under Build Rx.`}
      </span>
    );
  }
  return (
    <select
      value={setId}
      aria-label={`${NUTRIENT_LABEL[nutrient]} Rx for ${fieldName}`}
      onChange={(e) => onPick(e.target.value)}
      className="w-full max-w-[24rem] rounded border border-line bg-canvas px-1.5 py-1 text-fg outline-none focus:border-accent"
    >
      <option value="">Don’t spread this field</option>
      {options.map((s, i) => (
        <option key={s.id} value={s.id}>
          {s.name}
          {i === 0 ? " (most recent)" : ""} —{" "}
          {new Date(s.createdAt).toLocaleDateString()}
        </option>
      ))}
    </select>
  );
}

export function SpreadRxTable({
  fields,
  nutrients,
  rows,
  hydrating,
  onPick,
}: {
  fields: DashboardField[];
  nutrients: SpreadRxNutrient[];
  rows: Record<string, SpreadRow | undefined>;
  hydrating: boolean;
  onPick: (fieldId: string, nutrient: SpreadRxNutrient, setId: string) => void;
}) {
  return (
    <>
      {nutrients.map((n) => (
        <div key={n}>
          {/* the nutrient heading only earns its space when there is more than one */}
          {nutrients.length > 1 && (
            <h3 className="mb-1 text-[12px] font-semibold text-fg">
              {NUTRIENT_LABEL[n]}
            </h3>
          )}
          <table className="w-full text-[12px]">
            <thead className="text-left text-[11px] text-muted">
              <tr>
                <th className="px-2 py-1 font-medium">Field</th>
                <th className="px-2 py-1 font-medium">Rx to spread</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => {
                const row = rows[`${f.field_id}|${n}`];
                return (
                  <tr
                    key={`${f.field_id}|${n}`}
                    className="border-t border-line align-top"
                  >
                    <td className="px-2 py-1.5">
                      <span className="block text-fg">{f.field_name}</span>
                    </td>
                    <td className="px-2 py-1.5">
                      <SpreadRxSelect
                        nutrient={n}
                        fieldName={f.field_name}
                        setId={row?.setId ?? ""}
                        options={setsFor(f.field_id, n)}
                        hydrating={hydrating}
                        onPick={(setId) => onPick(f.field_id, n, setId)}
                      />
                      {row?.loading && (
                        <Loader2
                          size={11}
                          className="ml-1 inline animate-spin text-accent"
                        />
                      )}
                      {row?.error && (
                        <span className="mt-0.5 block text-[10px] text-amber-500">
                          {row.error}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}
